import { AsyncActionStatus, ThunkAction } from './common.actionTypes';
import {
  FetchWorkloadsAction,
  FETCH_WORKLOADS,
  FetchTeachingFormatsAction,
  FETCH_TEACHING_FORMATS,
  SearchMindItemsAction,
  SearchProductsAction,
  SearchVideoMindItemsAction,
  SearchTopicsAction,
  SearchRelatedTopicsAction,
  SearchUnifiedAction,
  SEARCH_MIND_ITEMS,
  SEARCH_VIDEO_MIND_ITEMS,
  SEARCH_PRODUCTS,
  SEARCH_UNIFIED,
  SEARCH_TOPICS,
  SEARCH_RELATED_TOPICS,
  SearchCourseMindItemsAction,
  SEARCH_COURSE_MIND_ITEMS,
} from './search.actionTypes';
import services from '../services';
import { hasNext } from '../utils/pagination';
import { SearchFilters, Pagination } from '../types/search';
import _ from 'lodash';
import type {
  RelatedTopicsRequest,
  SearchExperiment,
  SearchRequest,
} from '@klab-berlin/api-sdk/lib/types/requests/Common';
import { buildRelatedTopicsCacheKey } from '../reducers/search.selectors';
import { defaultSearchFilters } from '../constants';
import { isSchoolSubdomain } from '../utils/subdomain';
import config from 'config';
import { ContentType, CourseMindItemSearchResult } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import { SearchResponseData } from '@klab-berlin/api-sdk/lib/types/responses/Common';
import queryToSearchFilters from '../utils/queryToSearchFilters';

export const SEARCH_EXPERIMENT_DEMOTE_READ_ONLY = 'demote-read-only' as unknown as SearchExperiment;
export const SEARCH_EXPERIMENT_DEEP_LINKING = 'deep-linking' as unknown as SearchExperiment;

export interface SearchOptions {
  clearPreviousResults?: boolean;
  excludePublishers?: boolean;
  populateProducts?: boolean;
  scoreAggregationMethod?: string;
  experiments?: SearchExperiment[];
  // SEAR-460: when true, kimuno filters/boosts subject/schoolType/classYear on
  // AI-tag fields instead of CP fields (APIv3 forwards it as a plain query param).
  useAiFilters?: boolean;
  pagination?: Pagination;
}

// Aborted requests are an intentional cancellation, not a failure: skip dispatching
// *.ERROR so the UI doesn't surface them as search failures. The next caller-driven
// dispatch (the reason we aborted) immediately fires *.START and resets loading state.
const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';

export function searchMindItems(
  filters: SearchFilters,
  searchOptions: SearchOptions = { clearPreviousResults: false },
  abortSignal?: AbortSignal,
): ThunkAction<SearchMindItemsAction> {
  return async (dispatch, getState) => {
    let request: SearchRequest;
    let payload: { filters: SearchFilters; pagination: Pagination };
    const pagination = searchOptions.pagination || getState().search.mindItems.pagination;

    if (shouldFetchNextResults(filters, getState().search.searchFilters, searchOptions)) {
      if (!hasNext(getState().search.mindItems.pagination)) { return; }

      request = {
        ...getState().search.searchFilters,
        offset: pagination.offset,
        limit: pagination.limit,
        excludePublishers: searchOptions.excludePublishers,
        scoreAggregationMethod: searchOptions.scoreAggregationMethod,
        populateProducts: searchOptions.populateProducts,
        experiments: searchOptions.experiments,
        useAiFilters: searchOptions.useAiFilters,
      };
      payload = {
        filters: getState().search.searchFilters || defaultSearchFilters,
        pagination
      };
    }
    else {
      pagination.offset = 0;
      request = {
        ...filters,
        offset: pagination.offset,
        limit: pagination.limit,
        excludePublishers: searchOptions.excludePublishers,
        scoreAggregationMethod: searchOptions.scoreAggregationMethod,
        populateProducts: searchOptions.populateProducts,
        experiments: searchOptions.experiments,
        useAiFilters: searchOptions.useAiFilters,
      };
      payload = { filters, pagination };
    }

    dispatch({ type: SEARCH_MIND_ITEMS.START, payload });

    // Don't fetch video mind items if it's on the school subdomain
    if (isSchoolSubdomain(window.location.hostname)) {
      request.contentType = ['document'] as ContentType[];
    }

    //exclude MindItems type COURSE from every default search query
    if (!request.contentType) { 
      request.contentType = defaultSearchFilters.contentType;
    }

    services.mindItem
      .search(request, abortSignal)
      .then((result) => {
        dispatch({ type: SEARCH_MIND_ITEMS.SUCCESS, result, payload });
      })
      .catch((error) => {
        if (isAbortError(error)) return;
        dispatch({ error, type: SEARCH_MIND_ITEMS.ERROR, payload });
      });
  };
}

export function searchVideoMindItems(
  filters: SearchFilters,
  searchOptions: SearchOptions = { clearPreviousResults: false },
  abortSignal?: AbortSignal,
): ThunkAction<SearchVideoMindItemsAction> {
  return async (dispatch, getState) => {
    let request: SearchRequest;
    let payload: { filters: SearchFilters; pagination: Pagination };
    const pagination = searchOptions.pagination || getState().search.videoMindItems.pagination;

    if (shouldFetchNextResults(filters, getState().search.searchFilters, searchOptions)) {
      if (!hasNext(getState().search.videoMindItems.pagination)) { return; }

      request = {
        ...getState().search.searchFilters,
        offset: pagination.offset,
        limit: pagination.limit,
        excludePublishers: searchOptions.excludePublishers,
        scoreAggregationMethod: searchOptions.scoreAggregationMethod,
        populateProducts: searchOptions.populateProducts,
        experiments: searchOptions.experiments,
        useAiFilters: searchOptions.useAiFilters,
      };
      payload = {
        filters: getState().search.searchFilters || defaultSearchFilters,
        pagination
      };
    }
    else {
      pagination.offset = 0;
      request = {
        ...filters,
        offset: pagination.offset,
        limit: pagination.limit,
        excludePublishers: searchOptions.excludePublishers,
        scoreAggregationMethod: searchOptions.scoreAggregationMethod,
        populateProducts: searchOptions.populateProducts,
        experiments: searchOptions.experiments,
        useAiFilters: searchOptions.useAiFilters,
      };
      payload = { filters, pagination };
    }

    dispatch({ type: SEARCH_VIDEO_MIND_ITEMS.START, payload });

    // Fetch only video mind items
    request.contentType = ['video'] as ContentType[];

    // If the user is on the school subdomain, only fetch videos from non-vimeo-publishers
    let shouldSendRequest = true;
    if (isSchoolSubdomain(window.location.hostname)) {
      if (request.publisher && request.publisher.length > 0) {
        const validSelectedPublishers = request.publisher
          .filter(publisher => config.schoolsApp.nonVimeoVideoPublishers.includes(publisher));
        if (validSelectedPublishers.length > 0) {
          request.publisher = validSelectedPublishers;
        } else {
          shouldSendRequest = false;
        }
      } else {
        request.publisher = config.schoolsApp.nonVimeoVideoPublishers;
      }
    }

    if (shouldSendRequest) {
      services.mindItem
        .search(request, abortSignal)
        .then((result) => {
          dispatch({ type: SEARCH_VIDEO_MIND_ITEMS.SUCCESS, result, payload });
        })
        .catch((error) => {
          if (isAbortError(error)) return;
          dispatch({ error, type: SEARCH_VIDEO_MIND_ITEMS.ERROR, payload });
        });
    } else {
      const emptySearchResult = {
        data: [],
        meta: {
          pagination: {
            total: 0
          }
        }
      };
      dispatch({ type: SEARCH_VIDEO_MIND_ITEMS.SUCCESS, result: emptySearchResult, payload });
    }
  };
}

export function searchCourseMindItems(
  filters: SearchFilters,
  searchOptions: SearchOptions = { clearPreviousResults: false },
  abortSignal?: AbortSignal,
): ThunkAction<SearchCourseMindItemsAction> {
  return async (dispatch, getState) => {
    let request: SearchRequest;
    let payload: { filters: SearchFilters; pagination: Pagination };
    const pagination = searchOptions.pagination || getState().search.courseMindItems.pagination;

    if (shouldFetchNextResults(filters, getState().search.courseSearchFilters, searchOptions)) {
      if (!hasNext(getState().search.courseMindItems.pagination)) { return; }

      request = {
        ...getState().search.courseSearchFilters,
        offset: pagination.offset,
        limit: pagination.limit,
        excludePublishers: searchOptions.excludePublishers,
        scoreAggregationMethod: searchOptions.scoreAggregationMethod,
      };
      payload = {
        filters: getState().search.courseSearchFilters || queryToSearchFilters(),
        pagination
      };
    }
    else {
      pagination.offset = 0;
      request = {
        ...filters,
        offset: pagination.offset,
        limit: pagination.limit,
        excludePublishers: searchOptions.excludePublishers,
        scoreAggregationMethod: searchOptions.scoreAggregationMethod,
      };
      payload = { filters, pagination };
    }

    dispatch({ type: SEARCH_COURSE_MIND_ITEMS.START, payload });

    // Fetch only course mind items
    request.contentType = ['course'] as ContentType[];

    services.mindItem
      .search(request, abortSignal)
      .then((result) => {
        dispatch({
          type: SEARCH_COURSE_MIND_ITEMS.SUCCESS,
          result: result as SearchResponseData<CourseMindItemSearchResult>,
          payload });
      })
      .catch((error) => {
        if (isAbortError(error)) return;
        dispatch({ error, type: SEARCH_COURSE_MIND_ITEMS.ERROR, payload });
      });

  };
}

export function searchProducts(
  filters: SearchFilters,
  searchOptions: SearchOptions = { clearPreviousResults: false },
  abortSignal?: AbortSignal,
): ThunkAction<SearchProductsAction> {
  return async (dispatch, getState) => {
    let request: SearchRequest;
    let payload: { filters: SearchFilters; pagination: Pagination };
    const pagination = searchOptions.pagination || getState().search.products.pagination;

    if (shouldFetchNextResults(filters, getState().search.searchFilters, searchOptions)) {
      if (!hasNext(getState().search.products.pagination)) {
        return;
      }
      request = {
        ...getState().search.searchFilters,
        offset: pagination.offset,
        limit: pagination.limit,
        excludePublishers: searchOptions.excludePublishers,
        scoreAggregationMethod: searchOptions.scoreAggregationMethod,
        experiments: searchOptions.experiments,
        useAiFilters: searchOptions.useAiFilters,
      };
      payload = {
        filters: getState().search.searchFilters || defaultSearchFilters,
        pagination
      };
    }
    else {
      pagination.offset = 0;
      request = {
        ...filters,
        offset: pagination.offset,
        limit: pagination.limit,
        excludePublishers: searchOptions.excludePublishers,
        scoreAggregationMethod: searchOptions.scoreAggregationMethod,
        experiments: searchOptions.experiments,
        useAiFilters: searchOptions.useAiFilters,
      };
      payload = { filters, pagination };
    }

    dispatch({ type: SEARCH_PRODUCTS.START, payload });

    services.product
      .search(request, abortSignal)
      .then((result) => {
        dispatch({ type: SEARCH_PRODUCTS.SUCCESS, result, payload });
      })
      .catch((error) => {
        if (isAbortError(error)) return;
        dispatch({ error, type: SEARCH_PRODUCTS.ERROR, payload });
      });
  };
}

export function searchUnified(
  filters: SearchFilters,
  searchOptions: SearchOptions = { clearPreviousResults: false },
  abortSignal?: AbortSignal,
): ThunkAction<SearchUnifiedAction> {
  return async (dispatch, getState) => {
    let request: SearchRequest;
    let payload: { filters: SearchFilters; pagination: Pagination };
    const pagination = searchOptions.pagination || getState().search.unified.pagination;

    if (shouldFetchNextResults(filters, getState().search.searchFilters, searchOptions)) {
      if (!hasNext(getState().search.unified.pagination)) {
        return;
      }
      request = {
        ...getState().search.searchFilters,
        offset: pagination.offset,
        limit: pagination.limit,
        excludePublishers: searchOptions.excludePublishers,
        scoreAggregationMethod: searchOptions.scoreAggregationMethod,
        experiments: searchOptions.experiments,
        useAiFilters: searchOptions.useAiFilters,
      };
      payload = {
        filters: getState().search.searchFilters || defaultSearchFilters,
        pagination: pagination
      };
    }
    else {
      pagination.offset = 0;
      request = {
        ...filters,
        offset: pagination.offset,
        limit: pagination.limit,
        excludePublishers: searchOptions.excludePublishers,
        scoreAggregationMethod: searchOptions.scoreAggregationMethod,
        experiments: searchOptions.experiments,
        useAiFilters: searchOptions.useAiFilters,
      };
      payload = { filters, pagination };
    }

    request.contentType = defaultSearchFilters.contentType;

    dispatch({ type: SEARCH_UNIFIED.START, payload });

    services.search
      .unifiedSearch(request, abortSignal)
      .then((result) => {
        dispatch({ type: SEARCH_UNIFIED.SUCCESS, result, payload });
      })
      .catch((error) => {
        if (isAbortError(error)) return;
        dispatch({ error, type: SEARCH_UNIFIED.ERROR, payload });
      });
  };
}

export function searchTopics(
  subject?: string,
): ThunkAction<SearchTopicsAction> {
  return async (dispatch, getState) => {
    if (!subject) {
      return;
    }
    const payload = { subject };
    if (getState().search.subTopics.terms[subject]) {
      return;
    }
    dispatch({ type: SEARCH_TOPICS.START, payload });
    services.mindItem
      .getTopics(subject)
      .then((result) => {
        dispatch({ type: SEARCH_TOPICS.SUCCESS, result, payload });
      })
      .catch((error) => {
        dispatch({ error, type: SEARCH_TOPICS.ERROR, payload });
      });
  };
}

// Dedupes concurrent fetches per cache key; aborted entries are treated as
// orphans so a fresh dispatch can replace them.
const inFlightRelatedTopics = new Map<string, AbortSignal | undefined>();

export function fetchRelatedTopics(
  params?: RelatedTopicsRequest,
  abortSignal?: AbortSignal,
): ThunkAction<SearchRelatedTopicsAction> {
  return async (dispatch, getState) => {
    if (!params?.term) return;
    const cacheKey = buildRelatedTopicsCacheKey(params);
    const payload = { params, cacheKey };
    if (getState().search.relatedTopics.terms[cacheKey]) {
      return;
    }
    if (inFlightRelatedTopics.has(cacheKey)) {
      const existingSignal = inFlightRelatedTopics.get(cacheKey);
      if (!existingSignal || !existingSignal.aborted) {
        return;
      }
    }
    if (abortSignal?.aborted) return;
    inFlightRelatedTopics.set(cacheKey, abortSignal);
    dispatch({ type: SEARCH_RELATED_TOPICS.START, payload });
    services.search
      .fetchRelatedTopics(params, abortSignal)
      .then((result) => {
        dispatch({ type: SEARCH_RELATED_TOPICS.SUCCESS, result, payload });
      })
      .catch((error) => {
        // An aborted request is superseded by a newer fetch, so swallow it (same
        // as the other search thunks). Surfacing it as ERROR would leave the
        // shared related-topics status stuck at ERROR, which suppresses the pills'
        // loading skeleton on the next term — most visibly when landing on the
        // search page from elsewhere. Genuine failures still dispatch ERROR.
        if (isAbortError(error)) return;
        dispatch({ error, type: SEARCH_RELATED_TOPICS.ERROR, payload });
      })
      .finally(() => {
        if (inFlightRelatedTopics.get(cacheKey) === abortSignal) {
          inFlightRelatedTopics.delete(cacheKey);
        }
      });
  };
}

export function fetchWorkloads(): ThunkAction<FetchWorkloadsAction> {
  return async (dispatch, getState) => {
    if (getState().search.workloads.status === AsyncActionStatus.READY) {
      return;
    }

    dispatch({ type: FETCH_WORKLOADS.START });

    return services.text.fetchWorkloads()
      .then(result => dispatch({ type: FETCH_WORKLOADS.SUCCESS, result }))
      .catch(error => dispatch({ type: FETCH_WORKLOADS.ERROR, error }));
  };
}

export function fetchTeachingFormats(): ThunkAction<FetchTeachingFormatsAction> {
  return async (dispatch, getState) => {
    if (getState().search.teachingFormats.status === AsyncActionStatus.READY) {
      return;
    }

    dispatch({ type: FETCH_TEACHING_FORMATS.START });

    return services.text.fetchTeachingFormats()
      .then(result => dispatch({ type: FETCH_TEACHING_FORMATS.SUCCESS, result }))
      .catch(error => dispatch({ type: FETCH_TEACHING_FORMATS.ERROR, error }));
  };
}

const shouldFetchNextResults = (
  filters: SearchFilters, searchFilters: SearchFilters | undefined, options: SearchOptions
) => {
  return _.isEqual(filters, searchFilters) && !options.clearPreviousResults;
};
